// ref: https://github.com/Donaldcwl/browser-image-compression
import imageCompression, { type Options } from "browser-image-compression";
import heic2any from "heic2any";

// サムネイル画像の圧縮ピクセルサイズ
const THUMBNAIL_COMPRESS_WIDTH = 1280;

// トーク用のサムネイル画像の圧縮ピクセルサイズ
const TALK_THUMBNAIL_COMPRESS_WIDTH = 512;

// トーク用のサムネイル画像の圧縮ファイルサイズ(webp)
const TALK_THUMBNAIL_COMPRESS_SIZE = 0.02;

// チャプター用のサムネイル画像の圧縮ピクセルサイズ
const CHAPTER_THUMBNAIL_COMPRESS_WIDTH = 100;

// チャプター用のサムネイル画像の圧縮ファイルサイズ(webp)
const CHAPTER_THUMBNAIL_COMPRESS_SIZE = 0.01;

// 画像ファイルの対応拡張子
export const acceptableImgExtensions: string[] = [".png", ".jpg", ".jpeg", ".webp", ".heic"];
// プロフィール画像の対応拡張子
export const acceptableProfileImgExtensions: string[] = [".png", ".jpg", ".jpeg", ".webp", ".gif", ".heic"];
// トークアップロードの対応拡張子
export const acceptableTalkUploadExtensions: string[] = [
  ".png",
  ".jpg",
  ".jpeg",
  ".webp",
  ".heic",
  ".mp4",
  ".mov",
  ".pdf",
];

// トークアップロード時の注意書き
export const talkUploadNotice = "ファイル形式はJPG、PNG、WEBP、HEIF(.HEIC)、MP4、MOV、PDFのみ";

// サムネイルアップロード時の注意書き
export const thumbnailUploadNotices = ["ファイル形式はJPG、PNG、WEBP、HEIF(.HEIC)のみ"];

// サムネイルファイルのルール
export const thumbnailFileRules = [
  (f: File) => {
    return isFileExtensionAcceptable(f, acceptableImgExtensions) || "ファイル形式はJPG、PNG、WEBP、HEIF(.HEIC)のみ";
  },
];

// ファイルの拡張子が許可されているかをチェック
export const isFileExtensionAcceptable = (file: File, acceptableExtensions: string[]) => {
  return acceptableExtensions.some(extension => file.name.toLowerCase().endsWith(extension));
};

// ブラウザがwebpの変換をサポートしているかをチェック
export const supportsConvertWebP = () => {
  const canvas = document.createElement("canvas");
  if (canvas.getContext && canvas.getContext("2d")) {
    // 'image/webp'がサポートされているかをチェック
    return canvas.toDataURL("image/webp").indexOf("data:image/webp") === 0;
  }
  return false;
};

// ファイルが画像ファイルかどうかを判定する
export const isImageFile = (file: File): boolean => {
  return ["image/jpeg", "image/png", "image/heic", "image/webp"].includes(file.type);
};

// ファイルが動画ファイルかどうかを判定する
export const isMovieFile = (file: File): boolean => {
  return ["video/mp4", "video/quicktime"].includes(file.type);
};

// ファイルがpdfファイルかどうかを判定する
export const isPdfFile = (file: File): boolean => {
  return ["application/pdf"].includes(file.type);
};

// Fileの拡張子を変更する
export const changeFileExtension = (file: File, ext: string): File => {
  const filename = file.name.replace(/\.[^/.]+$/, ext);
  const fileToReturn = new File([file], filename, { type: file.type });
  return fileToReturn;
};

// プロフィール用の画像ファイルを作成する
export const convertProfileImage = async (file: File): Promise<File> => {
  return await img2webp(file, {
    // https://invinc.slack.com/archives/C04HG0404HJ/p1708322190930689?thread_ts=1706006073.718849&cid=C04HG0404HJ
    maxWidthOrHeight: 300, // 最大ピクセルサイズ
  });
};

// コンテンツ用の画像ファイルを作成する
export const convertContentImage = async (file: File, trimming: boolean = true): Promise<File> => {
  const file2 = trimming ? await trimmingImage(file, THUMBNAIL_COMPRESS_WIDTH) : file;
  return await img2webp(file2, {
    maxWidthOrHeight: THUMBNAIL_COMPRESS_WIDTH, // 最大ピクセルサイズ
  });
};

// 講座詳細用の画像ファイルを作成する
export const convertPlanDetailImage = async (file: File): Promise<File> => {
  return await img2webp(file, {
    maxWidthOrHeight: THUMBNAIL_COMPRESS_WIDTH, // 最大ピクセルサイズ
  });
};

export const getChapterThumbnailConvertOptions = (): Options => {
  // webp 変換サポートあり -> 解像度およびファイルサイズ調整つき webp 変換する
  // webp 変換サポートなし -> 解像度のみ調整
  return supportsConvertWebP()
    ? {
        maxSizeMB: CHAPTER_THUMBNAIL_COMPRESS_SIZE, // 最大ファイルサイズ
        maxWidthOrHeight: CHAPTER_THUMBNAIL_COMPRESS_WIDTH, // 最大ピクセルサイズ
      }
    : {
        maxWidthOrHeight: CHAPTER_THUMBNAIL_COMPRESS_WIDTH, // 最大ピクセルサイズ
      };
};
// チャプター用の画像ファイルを作成する
export const convertChapterImage = async (data: string): Promise<string> => {
  // img2webpはfileを受け取るため、base64をblobに変換し、fileを作成する
  const blob = base64toBlob(data, "image/png");
  const file = new File([blob], "chapter-thumbnail.png", { type: "image/png" });
  const compressedFile = await img2webp(file, getChapterThumbnailConvertOptions());

  return new Promise<string>(resolve => {
    const fileReader = new FileReader();
    fileReader.onload = function () {
      resolve(this.result as string);
    };
    fileReader.readAsDataURL(compressedFile);
  });
};

// トーク用の画像ファイルを作成する
export const convertTalkImage = async (file: File): Promise<File> => {
  return await img2webp(file, {});
};

// トーク用のサムネイルファイル作成用オプションを取得
export const getTalkThumbnailConvertOptions = (): Options => {
  // webp 変換サポートあり -> 解像度およびファイルサイズ調整つき webp 変換する
  // webp 変換サポートなし -> 解像度のみ調整。画像形式およびファイルサイズそのままとして BE 側で webp 変換する
  // https://fincs.backlog.com/view/FINCS-3488
  return supportsConvertWebP()
    ? {
        maxSizeMB: TALK_THUMBNAIL_COMPRESS_SIZE, // 最大ファイルサイズ
        maxWidthOrHeight: TALK_THUMBNAIL_COMPRESS_WIDTH, // 最大ピクセルサイズ
      }
    : {
        maxWidthOrHeight: TALK_THUMBNAIL_COMPRESS_WIDTH, // 最大ピクセルサイズ
      };
};

// トーク用のサムネイルファイルを作成する
// base64を返す
export const convertTalkThumbnail = async (file: File): Promise<string> => {
  const compressedFile = await img2webp(file, getTalkThumbnailConvertOptions());

  const toBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });

  const base64File = await toBase64(compressedFile);
  return base64File;
};

// トーク用のサムネイルファイルを作成する
// base64を返す
export const convertTalkMovieThumbnail = (base64Str: string, callback: (resizedBase64: string) => void) => {
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement("canvas");
    let width = img.width;
    let height = img.height;

    // Calculate new dimensions while maintaining aspect ratio
    if (width > height) {
      if (width > TALK_THUMBNAIL_COMPRESS_WIDTH) {
        height *= TALK_THUMBNAIL_COMPRESS_WIDTH / width;
        width = TALK_THUMBNAIL_COMPRESS_WIDTH;
      }
    } else if (height > TALK_THUMBNAIL_COMPRESS_WIDTH) {
      width *= TALK_THUMBNAIL_COMPRESS_WIDTH / height;
      height = TALK_THUMBNAIL_COMPRESS_WIDTH;
    }

    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx!.drawImage(img, 0, 0, width, height);

    // Get the new Base64 Data URL
    callback(canvas.toDataURL());
  };
  img.src = base64Str;
};

// 画像ファイルをファイル形式そのままで圧縮する
const imgCompress = async (file: File, options: Options) => {
  const mergeOptions = {
    ...options,
    useWebWorker: true, // trueにするとマルチスレッドで動く
  };
  return await imageCompression(file, mergeOptions);
};

// 画像ファイルをwebpに変換する
const img2webp = async (file: File, options: Options) => {
  // imageCompression は HEIC に対応していないため、HEIC の場合は変換する
  const fileToCompress = file.name.toLowerCase().endsWith(".heic") ? await heic2jpg(file) : file;

  // webp がサポートされていない場合は、画像形式そのままで圧縮する
  const webpSupported = supportsConvertWebP();
  if (!webpSupported) {
    return await imgCompress(fileToCompress as File, options);
  }

  const mergeOptions = {
    ...options,
    useWebWorker: true, // trueにするとマルチスレッドで動く
    fileType: "image/webp",
    initialQuality: 0.8, // 画像の品質設定（0から1の範囲で設定、高いほど品質が良くなる）
  };
  const compressedFile = await imageCompression(fileToCompress as File, mergeOptions);
  return changeFileExtension(compressedFile, ".webp");
};

// HEICファイルをJPGファイルに変換する
const heic2jpg = (heicFile: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event: any) => {
      const blob = new Blob([event.target.result]);
      heic2any({
        blob,
        toType: "image/jpeg",
      })
        .then(conversionResult => {
          const jpgFile = new File([conversionResult as Blob], heicFile.name + ".jpg", {
            type: "image/jpeg",
          });
          resolve(jpgFile);
        })
        .catch((error: any) => {
          reject(error);
        });
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(heicFile);
  });
};

// 画像をトリミングする
const trimmingImageData = (data: string, fileType: string, maxWidth: number): Promise<string> => {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const aspect = 16 / 9;
      const width = Math.min(img.width, maxWidth);
      const height = width / aspect;

      // Canvasのサイズを設定
      canvas.width = width;
      canvas.height = height;

      // 元画像のトリミング対象部分を計算
      const drawWidth = img.width;
      const drawHeight = img.width / aspect;

      // 描画する画像のY座標を計算（中央にするために）
      const drawY = (img.height - drawHeight) / 2;

      // 中央に上下をトリミングして描画
      ctx?.drawImage(
        img,
        0,
        drawY,
        drawWidth,
        drawHeight, // 元画像のトリミング部分
        0,
        0,
        width,
        height, // キャンバスへの描画範囲
      );

      resolve(canvas.toDataURL(fileType));
    };
    img.src = data;
  });
};

// 画像をトリミングする
export const trimmingImage = async (file: File, maxWidth: number): Promise<File> => {
  // imageCompression は HEIC に対応していないため、HEIC の場合は変換する
  const fileToCompress = file.name.toLowerCase().endsWith(".heic") ? await heic2jpg(file) : file;
  return new Promise<File>(resolve => {
    const fileReader = new FileReader();
    fileReader.onload = async function () {
      const img = await trimmingImageData(this.result as string, fileToCompress.type, maxWidth);
      // Fileに変換
      const blob = base64toBlob(img, fileToCompress.type);
      resolve(new File([blob], fileToCompress.name, { type: fileToCompress.type }));
    };
    fileReader.readAsDataURL(fileToCompress);
  });
};

// Base64からBlobを作成する
const base64toBlob = (base64: string, fileType: string) => {
  // Base64からバイナリへ変換
  const bin = atob(base64.split(",")[1]);
  const buffer = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    buffer[i] = bin.charCodeAt(i);
  }
  // Blobを作成
  const blob = new Blob([buffer.buffer], { type: fileType });
  return blob;
};

// Fileからプレビュー画像を取得する
export const previewImage = async (file: File, trimming: boolean = true): Promise<string> => {
  // 画像をプレビュー表示するために、画像をリサイズする
  let file2 = file;
  if (trimming) {
    file2 = await trimmingImage(file, THUMBNAIL_COMPRESS_WIDTH);
  } else if (file.name.toLowerCase().endsWith(".heic")) {
    file2 = await heic2jpg(file);
  }

  return new Promise<string>(resolve => {
    const fileReader = new FileReader();
    fileReader.onload = function () {
      resolve(this.result as string);
    };
    fileReader.readAsDataURL(file2);
  });
};

declare module "@vue/runtime-core" {
  interface ComponentCustomProperties {
    acceptableImgExtensions: string[];
    acceptableProfileImgExtensions: string[];
    acceptableTalkUploadExtensions: string[];
    thumbnailUploadNotices: string[];
    thumbnailFileRules: ((f: File) => string | boolean)[];
    isFileExtensionAcceptable: (file: File, acceptableExtensions: string[]) => boolean;
    supportsConvertWebP: () => boolean;
    isImageFile: (file: File) => boolean;
    isPdfFile: (file: File) => boolean;
    changeFileExtension: (file: File, ext: string) => File;
    convertProfileImage: (file: File) => Promise<File>;
    convertContentImage: (file: File, trimming?: boolean) => Promise<File>;
    convertPlanDetailImage: (file: File) => Promise<File>;
    convertTalkImage: (file: File) => Promise<File>;
    getTalkThumbnailConvertOptions: () => Options;
    convertTalkThumbnail: (file: File) => Promise<string>;
    convertTalkMovieThumbnail: (
      base64Str: string,
      callback: (resizedBase64: string) => void,
    ) => void;
    imgCompress: (file: File, options: Options) => Promise<File>;
    img2webp: (file: File, options: Options) => Promise<File>;
    heic2jpg: (heicFile: File) => Promise<File>;
    trimmingImage: (file: File, maxWidth: number) => Promise<File>;
    base64toBlob: (base64: string, fileType: string) => Blob;
    previewImage: (file: File, trimming?: boolean) => Promise<string>;
  }
}
